'use client';

import React, { useEffect, useRef, useState, FormEvent, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, usePathname } from 'next/navigation';
import { showsClient, type ShowCitySuggestion, type ShowNameSuggestion } from '@bip/api-client';
import { getCurrentShowsParams, replaceShowsSearchParams } from '../_lib/useShowsQueryParams';
import { IMG } from '../../imgPrefix';
import { SHOWS_BASE_PATH } from '../config';
import { getEffectiveBookingDate } from '../_lib/dateUtils';

const FALLBACK_IMAGE = `${IMG}/image/no-image.png`;

// Project's own asset mirror of the CMS hero image (theaters-cms-reference-
// code/pages/Shows.html `.hero-image-shape img`) — served from this
// project's own CDN host rather than the external CMS cloudfront URL.
const HERO_IMAGE = 'https://bookitplease.optimiser.site/image/theatre-top-imgnew1.png?v=qvYUx';

// Matches the Restaurants domain's search bar (RestaurantsHero.tsx) — same
// debounce delay and minimum query length, so both feel equally responsive.
const SUGGEST_DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 3;
/** Show-name typeahead starts from the FIRST character, matching the CMS
 *  (`theatreLandingPage.js` sets jQuery autocomplete `minLength: 1`). The
 *  city field keeps `MIN_QUERY_LENGTH` — it searches a far larger
 *  SearchLocation collection where a 1-char query is mostly noise, whereas
 *  the show list is scoped to the pinned city and stays small. */
const SHOW_MIN_QUERY_LENGTH = 1;
/** Max show suggestions rendered in the dropdown. Matches the CMS
 *  (`get-theatre-search-suggestions.js` defaults `limit` to 5 for
 *  `type === "show"`): beyond this the list is capped and a "Show all
 *  results" row is appended instead of growing the panel. */
const SHOW_SUGGESTION_LIMIT = 5;

/**
 * Today + tomorrow as `YYYY-MM-DD` strings — same format the hotels SearchBar writes into
 * `localStorage['searches']`. Verbatim copy of `RestaurantsHero.tsx`'s own helper (each domain
 * duplicates this tiny util rather than sharing it — same convention as the rest of this file).
 */
function getCurrentAndNextDay(): { startDate: string; endDate: string } {
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(start);
  end.setDate(start.getDate() + 1);
  return { startDate: fmt(start), endDate: fmt(end) };
}

/**
 * Hotels' own `SearchBar`/home page NEVER reads `theatre-searches-city` — it only reads the
 * `searches` array (see `SearchBarWrapper.tsx`'s `loadRecent()[0]`). Writing only the city blob
 * (as this file originally did) is why a Shows city pick reached Sports/Restaurants but never
 * Hotels. Verbatim port of `RestaurantsHero.tsx`'s own `searches[]` seed on submit — same shape
 * (`dest_id/checkin/checkout/rooms/adults/children/ratecode/_label`), same dedup-by-`dest_id`
 * + unshift + cap-at-3 behavior.
 */
function seedHotelsRecentSearch(suggestion: ShowCitySuggestion) {
  try {
    const { startDate, endDate } = getCurrentAndNextDay();
    const newSearch = {
      dest_id: suggestion.id,
      checkin: startDate,
      checkout: endDate,
      rooms: 1,
      adults: '2',
      children: '',
      ratecode: '',
      _label: [suggestion.city, suggestion.state, suggestion.country].filter(Boolean).join(', '),
    };
    const previous = JSON.parse(localStorage.getItem('searches') || '[]');
    const updated = (Array.isArray(previous) ? previous : []).filter(
      (r: { dest_id?: string }) => r?.dest_id !== newSearch.dest_id,
    );
    updated.unshift(newSearch);
    if (updated.length > 3) updated.pop();
    localStorage.setItem('searches', JSON.stringify(updated));
  } catch {
    // localStorage disabled (private browsing, quota) — safe to ignore.
  }
}

/**
 * Renders `children` into `document.body` via a portal, positioned to
 * track `anchorRef`'s own bottom-left/width (replicates the dropdown's
 * previous `position: absolute; top: calc(100% + 8px); left: 0; right: 0`
 * relative to the search field — see the `--portal` CSS override below).
 *
 * Why a portal: `.shows-hero` (an ancestor of the search bar) needs
 * `overflow: hidden` to contain its own background image/notch mask, and
 * the page's `<main>` wrapper carries `overflow-x: hidden` — both clip a
 * normal `position: absolute` descendant that grows past their bounds.
 * That's the actual reported bug (the suggestions list rendering behind/
 * clipped by the card grid below the hero) — no `z-index` value fixes a
 * clipping ancestor, only a stacking one. Escaping via a portal sidesteps
 * every ancestor's overflow/stacking-context constraints at once, rather
 * than chasing each one (and needing to re-chase it if a future change
 * adds another `overflow`/`transform`/`filter` ancestor).
 *
 * Repositions on scroll (capture-phase, so it also catches scrolling
 * inside `<main>`'s own `overflow-y: auto`) and on resize, so it tracks
 * the field across breakpoints and while the page moves.
 */
function AnchoredPortal({
  anchorRef,
  open,
  children,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  children: ReactNode;
}) {
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!open) return;

    function update() {
      const el = anchorRef.current;
      if (!el || typeof document === 'undefined') return;
      const r = el.getBoundingClientRect();
      const bodyRect = document.body.getBoundingClientRect();
      setRect({
        top: r.bottom - bodyRect.top + 8,
        left: r.left - bodyRect.left,
        width: r.width,
      });
    }

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    }

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && anchorRef.current) {
      ro = new ResizeObserver(update);
      ro.observe(anchorRef.current);
    }

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      if (vv) {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      }
      ro?.disconnect();
    };
  }, [open, anchorRef]);

  if (!open || !rect || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="shows-search-portal-wrapper"
      style={{ position: 'absolute', top: rect.top, left: rect.left, width: rect.width, zIndex: 9999 }}
    >
      {children}
    </div>,
    document.body,
  );
}

/**
 * Hero + search bar — background image, heading/subtext, and the dual
 * city/show-name search layout all lifted from
 * `theaters-cms-reference-code/pages/Shows.html` (`.hero-wrapper`,
 * `.theatreimgsection`, `.theatre-search-grid`) per the Guiding Principle
 * in DOCS/shows/plan.md: UI/copy ported verbatim, only the underlying
 * fetch/routing logic is this repo's own.
 *
 * `.shows-hero-outer` contains the hero to the same page gutters as the
 * card grid below it (NOT full-bleed to the viewport edge) — per the
 * reference design, the hero image itself is a notched "card" sitting
 * inside the page, with the page background visible on all 4 sides, same
 * 4-corner notch family as `.shows-card`.
 *
 * The heading uses an explicit line break ("Experience the Magic of" /
 * "Theatre") to match the reference pixel-for-pixel regardless of viewport
 * width, rather than relying on responsive reflow to happen to wrap there.
 *
 * City search now has a real ranked typeahead (`shows-service`
 * `/suggestions/cities`, backed by the shared `CustomCollection_
 * SearchLocation_WLLT9` collection) — selecting a suggestion pins it as a
 * clearable "chip" showing the full "City, State, Country" label, while the
 * ACTUAL `city` query param sent on submit stays the plain city name (the
 * backend's `city` filter is still a substring match against `Show.City`,
 * so the full label would never match). Free-typed text without picking a
 * suggestion still works exactly as before. Show-name search gets the same
 * typeahead treatment (`/suggestions/shows`, name + thumbnail image) but
 * fills the field with the show's name rather than pinning a chip — the
 * `q` filter already substring-matches the typed name.
 *
 * Both dropdowns' markup/CSS mirror the Restaurants domain's search
 * suggestion panel (`RestaurantsHero.tsx` + `restaurants.css`) — same
 * loading spinner / "No result found" states, row layout, and hover style —
 * so every domain's typeahead feels like one design system.
 *
 * City defaults to London (`DEFAULT_CITY` below) — pinned as the same
 * clearable "chip" a real typeahead selection produces, showing the full
 * "London, Greater London, United Kingdom" label — whenever the page loads
 * with no `city` URL param. Clearing it (the chip's own × button) empties
 * the field so the user can search a different city.
 *
 * City is a required precondition for show-name search (explicit product
 * requirement) — but the show-name field is NOT the native `disabled`
 * attribute (that made it unfocusable/visually dead, an explicit product
 * ask to avoid). Instead its `onChange` simply no-ops while `!hasCity`: the
 * field stays focusable/clickable, typed characters just don't register
 * (the controlled input's value never changes), and the dimmed
 * `--disabled` CSS class stays as a visual hint only. With the London
 * default above this only actually matters right after the city chip is
 * cleared. Submitting with no city shows "City is required" as a small red
 * line stacked directly under the (unchanged) "Search by City" placeholder,
 * INSIDE the bar — not a border/box-shadow ring around the field (that used
 * to visually clash with the pill's rounded corners, a squared-off ring
 * poking out past the curve) and not a placeholder swap. The bar's height
 * is fixed to comfortably fit this two-line state so it never resizes when
 * the error appears/clears.
 */

/** Default city pin — same shape a real `/suggestions/cities` pick
 *  produces (`ShowCitySuggestion`), so it renders as the identical chip.
 *  `id` is a stable local placeholder, not a real record id — nothing
 *  reads `selectedCity.id` (submission uses `.city` only), so it doesn't
 *  need to match a real document in any given environment's DB. */
const DEFAULT_CITY: ShowCitySuggestion = {
  id: 'default-london',
  city: 'London',
  state: 'Greater London',
  country: 'United Kingdom',
  label: 'London, Greater London, United Kingdom',
};
export function ShowsHero() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [city, setCity] = useState(
    searchParams.get('city')
      ? (searchParams.get('city') === DEFAULT_CITY.city ? DEFAULT_CITY.label : searchParams.get('city')!)
      : DEFAULT_CITY.label
  );
  const [show, setShow] = useState(searchParams.get('q') || '');
  const [selectedCity, setSelectedCity] = useState<ShowCitySuggestion | null>(
    searchParams.get('city')
      ? (searchParams.get('city') === DEFAULT_CITY.city ? DEFAULT_CITY : null)
      : DEFAULT_CITY,
  );
  const [cityError, setCityError] = useState<string | null>(null);

  const [cityResults, setCityResults] = useState<ShowCitySuggestion[]>([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [, setCityLoading] = useState(false);
  const [showResults, setShowResults] = useState<ShowNameSuggestion[]>([]);
  // True when the query matched MORE shows than `SHOW_SUGGESTION_LIMIT` —
  // renders the CMS's "Show all results" row under the capped list.
  const [showHasMore, setShowHasMore] = useState(false);
  const [showOpen, setShowOpen] = useState(false);
  const [, setShowLoading] = useState(false);
  // Mirrors `selectedCity`'s role for the city field: once a suggestion has
  // been explicitly picked, suppresses the debounced-search effect below
  // from re-firing on that same value (it depends on `show`, which
  // `selectShow` itself changes) and popping the dropdown open again right
  // after selection — cleared the moment the user edits the field again so
  // typing still searches as before.
  const [selectedShow, setSelectedShow] = useState(false);

  const cityFieldRef = useRef<HTMLDivElement>(null);
  const showFieldRef = useRef<HTMLDivElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const showInputRef = useRef<HTMLInputElement>(null);
  // Refs to the portaled dropdown <ul>s themselves — now rendered into
  // `document.body` (see `AnchoredPortal`), so they're no longer inside
  // `cityFieldRef`/`showFieldRef`'s own DOM subtree. The outside-click
  // check below must treat "inside the dropdown" as "not outside" too,
  // or clicking a suggestion would register as an outside click on
  // `mousedown` and close the list before its own `onClick` (selectCity/
  // selectShow, which fires later on `mouseup`) ever runs.
  const cityDropdownRef = useRef<HTMLUListElement>(null);
  const showDropdownRef = useRef<HTMLUListElement>(null);

  const hasCity = Boolean(selectedCity) || city.trim().length > 0;

  useEffect(() => {
    const cityParam = searchParams.get('city');

    if (cityParam) {
      if (selectedCity && selectedCity.city === cityParam) {
        setCity(selectedCity.label);
      } else if (cityParam === DEFAULT_CITY.city) {
        setSelectedCity(DEFAULT_CITY);
        setCity(DEFAULT_CITY.label);
      } else {
        setSelectedCity(null);
        setCity(cityParam);
      }
    } else {
      // Cross-domain "last searched city" — Sports/Hotels/Restaurants all read/write a shared
      // `theatre-searches-city` blob in localStorage so picking a city on any one of them
      // pre-fills the others; Shows never participated in this at all. Same priority-1 check
      // those domains use (their own priority-2 IP-geo/priority-3 hardcoded-fallback steps are
      // each domain's own separate concern, not part of this shared contract, so not replicated
      // here) — only takes effect when the URL didn't already carry `?city=`.
      let usedShared = false;
      try {
        const raw = localStorage.getItem('theatre-searches-city');
        if (raw) {
          const parsed = JSON.parse(raw) as { city?: string; state?: string; country?: string; dest_id?: string } | null;
          const lastCity = parsed?.city?.trim();
          if (lastCity) {
            const shared: ShowCitySuggestion = {
              id: parsed?.dest_id?.trim() || DEFAULT_CITY.id,
              city: lastCity,
              state: parsed?.state || undefined,
              country: parsed?.country || undefined,
              label: [lastCity, parsed?.state, parsed?.country].filter(Boolean).join(', '),
            };
            setSelectedCity(shared);
            setCity(shared.label);
            usedShared = true;

            const activeParams = getCurrentShowsParams() ?? new URLSearchParams(searchParams.toString());
            if (activeParams.get('city') !== lastCity) {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('bip-shows-search-start'));
              }
              const nextParams = new URLSearchParams(activeParams.toString());
              nextParams.set('city', lastCity);
              nextParams.delete('page');
              replaceShowsSearchParams(pathname, nextParams);
            }
          }
        }
      } catch {
        // localStorage disabled — fall through to the London default below.
      }

      if (!usedShared) {
        setSelectedCity(DEFAULT_CITY);
        setCity(DEFAULT_CITY.label);
      }
    }

    setShow(searchParams.get('q') || '');
    setSelectedShow(false);
  }, [pathname, searchParams.get('city'), searchParams.get('q')]);

  // Listen for storage / window focus changes so coming back from another tab searches that city
  useEffect(() => {
    function syncFromStorage() {
      const cityParam = searchParams.get('city');
      if (cityParam) return;
      if (document.activeElement === cityInputRef.current) return;

      try {
        const raw = localStorage.getItem('theatre-searches-city');
        if (raw) {
          const parsed = JSON.parse(raw) as { city?: string; state?: string; country?: string; dest_id?: string } | null;
          const lastCity = parsed?.city?.trim();
          if (lastCity && (!selectedCity || selectedCity.city !== lastCity)) {
            const shared: ShowCitySuggestion = {
              id: parsed?.dest_id?.trim() || DEFAULT_CITY.id,
              city: lastCity,
              state: parsed?.state || undefined,
              country: parsed?.country || undefined,
              label: [lastCity, parsed?.state, parsed?.country].filter(Boolean).join(', '),
            };
            setSelectedCity(shared);
            setCity(shared.label);

            const activeParams = getCurrentShowsParams() ?? new URLSearchParams(searchParams.toString());
            if (activeParams.get('city') !== lastCity) {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('bip-shows-search-start'));
              }
              const nextParams = new URLSearchParams(activeParams.toString());
              nextParams.set('city', lastCity);
              nextParams.delete('page');
              replaceShowsSearchParams(pathname, nextParams);
            }
          }
        }
      } catch {
        // localStorage disabled
      }
    }

    window.addEventListener('storage', syncFromStorage);
    window.addEventListener('focus', syncFromStorage);
    return () => {
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener('focus', syncFromStorage);
    };
  }, [pathname, searchParams, selectedCity]);

  // Close either dropdown on an outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent | PointerEvent) {
      const target = e.target as Node;
      const insideCity =
        cityFieldRef.current?.contains(target) || cityDropdownRef.current?.contains(target);
      const insideShow =
        showFieldRef.current?.contains(target) || showDropdownRef.current?.contains(target);
      if (!insideCity) setCityOpen(false);
      if (!insideShow) setShowOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('pointerdown', onDocClick);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('pointerdown', onDocClick);
    };
  }, []);

  // Debounced city suggestions — opens the dropdown (with a loading spinner)
  // immediately once the minimum length is met, same UX as the Restaurants
  // search bar, rather than waiting for the response to pop the dropdown open.
  useEffect(() => {
    if (selectedCity || city.trim().length < MIN_QUERY_LENGTH) {
      setCityResults([]);
      setCityOpen(false);
      setCityLoading(false);
      return;
    }
    setCityOpen(true);
    setCityLoading(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await showsClient.citySuggestions(city.trim());
        if (!cancelled) setCityResults(results);
      } catch {
        if (!cancelled) setCityResults([]);
      } finally {
        if (!cancelled) setCityLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [city, selectedCity]);

  // Show-name search is gated on a city being present first.
  useEffect(() => {
    if (!hasCity) {
      setShowOpen(false);
      setShowResults([]);
      setShowHasMore(false);
      setShowLoading(false);
    }
  }, [hasCity]);

  // Debounced show-name suggestions — same immediate-open-then-load pattern.
  // `selectedShow` short-circuits this: without it, picking a suggestion
  // sets `show` to that suggestion's own name, which is itself a change to
  // this effect's `show` dependency — re-firing it and popping the dropdown
  // back open (searching for the exact name just picked, so it showed only
  // that one match) immediately after the user had just closed it by
  // selecting.
  useEffect(() => {
    if (!hasCity || selectedShow || show.trim().length < SHOW_MIN_QUERY_LENGTH) {
      setShowResults([]);
      setShowHasMore(false);
      setShowOpen(false);
      setShowLoading(false);
      return;
    }
    setShowOpen(true);
    setShowLoading(true);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { items, hasMore } = await showsClient.showSuggestions(show.trim(), SHOW_SUGGESTION_LIMIT);
        if (!cancelled) {
          setShowResults(items);
          setShowHasMore(hasMore);
        }
      } catch {
        if (!cancelled) {
          setShowResults([]);
          setShowHasMore(false);
        }
      } finally {
        if (!cancelled) setShowLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [show, hasCity, selectedShow]);

  function selectCity(suggestion: ShowCitySuggestion) {
    setSelectedCity(suggestion);
    setCity(suggestion.label);
    setCityOpen(false);
    setCityResults([]);
    setCityError(null);
    setShowOpen(false);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('bip-shows-search-start'));
    }

    // Seed the shared cross-domain "last searched city" blob — same shape/key Sports/Hotels/
    // Restaurants write on their own city pick (`city/country/id/state/dest_id`), so choosing a
    // city here also pre-fills theirs. Only on a REAL suggestion pick (has a real SearchLocation
    // `_id` from `/suggestions/cities`) — never for the passive `DEFAULT_CITY` placeholder, same
    // "strict shape only" rule the other domains follow (their own hardcoded-default fallback
    // doesn't write this either).
    try {
      localStorage.setItem(
        'theatre-searches-city',
        JSON.stringify({
          city: suggestion.city,
          country: suggestion.country || '',
          id: suggestion.id,
          state: suggestion.state || '',
          dest_id: suggestion.id,
        }),
      );
    } catch {
      // localStorage disabled (private browsing, quota) — safe to ignore.
    }

    // Hotels-domain `searches[]` seed — see `seedHotelsRecentSearch`'s doc comment. Without this,
    // Sports/Restaurants pick up the city (they read `theatre-searches-city` above) but Hotels
    // never does, since it only ever reads this separate key.
    seedHotelsRecentSearch(suggestion);

    const params = new URLSearchParams(searchParams.toString());
    params.set('city', suggestion.city);
    if (show.trim()) params.set('q', show.trim());
    else params.delete('q');
    params.delete('page');
    replaceShowsSearchParams(pathname, params);
  }

  function selectShow(suggestion: ShowNameSuggestion) {
    setShow(suggestion.name);
    setSelectedShow(true);
    setShowOpen(false);
    setShowResults([]);
    setShowHasMore(false);
    if (typeof window !== 'undefined') {
      const targetDate = getEffectiveBookingDate(searchParams);
      const url = targetDate
        ? `${SHOWS_BASE_PATH}/${suggestion.slug}?bookingDate=${encodeURIComponent(targetDate)}`
        : `${SHOWS_BASE_PATH}/${suggestion.slug}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * "Show all results" row — CMS `theatreLandingPage.js` `handleShowAllResults()`.
   * Does NOT open a show detail page: it runs the full listing search for
   * whatever is typed, scoped to the pinned city, exactly like pressing
   * Search. Mirrors `handleSubmit`'s URL-param write so back/forward and
   * refresh keep working (search state lives in the URL).
   */
  function handleShowAllResults() {
    setShowOpen(false);
    setShowResults([]);
    setShowHasMore(false);
    // Suppresses the debounced typeahead from immediately re-opening the
    // dropdown for the same term the user just acted on.
    setSelectedShow(true);

    const cityValue = (selectedCity?.city || city).trim();
    if (!cityValue) {
      setCityError('City is required');
      cityInputRef.current?.focus();
      return;
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('bip-shows-search-start'));
    }

    const params = new URLSearchParams(searchParams.toString());
    params.set('city', cityValue);
    if (show.trim()) params.set('q', show.trim());
    else params.delete('q');
    params.delete('page');
    replaceShowsSearchParams(pathname, params);
  }

  /**
   * Drops the show-name keyword from the ACTIVE listing search and reloads
   * the unfiltered list. Used both by the × button and by manually deleting
   * the field down to empty — after a "Show all results" (or Search) the
   * listing is filtered by `q`, so simply blanking the input would leave
   * the filtered results on screen with an empty search box.
   *
   * Reads `getCurrentShowsParams()` rather than `searchParams`: the listing
   * keeps its params in memory and the browser URL is wiped to `/shows`, so
   * `useSearchParams()` here can't see the `q` that's actually applied.
   *
   * No-ops when no keyword is applied, so clearing an already-unfiltered
   * field never triggers a pointless shimmer + refetch.
   */
  function clearShowQueryFilter() {
    const active = getCurrentShowsParams() ?? new URLSearchParams(searchParams.toString());
    if (!active.get('q')) return;

    active.delete('q');
    // Back to page 1 — the old offset belongs to the filtered result set.
    active.delete('page');

    if (typeof window !== 'undefined') {
      // Shimmer: ShowsList clears its records and shows skeletons until the
      // refetch triggered by the params change below resolves.
      window.dispatchEvent(new Event('bip-shows-search-start'));
    }
    replaceShowsSearchParams(pathname, active);
  }

  function handleClearCity(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    setCity('');
    setSelectedCity(null);
    setCityError(null);
    setCityResults([]);
    setCityOpen(false);
    cityInputRef.current?.focus();
    // Matches Sports'/Restaurants' own clear-button behavior — the shared blob shouldn't keep
    // re-seeding a city the user just explicitly cleared.
    try {
      localStorage.removeItem('theatre-searches-city');
    } catch {
      // localStorage disabled — safe to ignore.
    }
  }

  function handleClearShow(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    setShow('');
    setSelectedShow(false);
    setShowResults([]);
    setShowHasMore(false);
    setShowOpen(false);
    showInputRef.current?.focus();
    clearShowQueryFilter();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const cityValue = (selectedCity?.city || city).trim();
    if (!cityValue) {
      setCityError('City is required');
      cityInputRef.current?.focus();
      return;
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('bip-shows-search-start'));
    }

    const params = new URLSearchParams(searchParams.toString());
    params.set('city', cityValue);
    if (show.trim()) params.set('q', show.trim());
    else params.delete('q');
    params.delete('page');
    setCityOpen(false);
    setShowOpen(false);
    replaceShowsSearchParams(pathname, params);
  }

  return (
    <div className="shows-container shows-hero-outer">
      <section className="shows-hero">
        <div className="shows-hero-bg">
          <img src={HERO_IMAGE} alt="" />
        </div>

        <div className="shows-hero-content">
          <h1 className="shows-hero-title font-display">Experience the Magic of Theatre</h1>
          <p className="shows-hero-subtitle mt-3 text-white">Book tickets for the best musicals, plays &amp; family shows.</p>

          <form onSubmit={handleSubmit} className="shows-search-bar mt-6" noValidate>
            <div className={`shows-search-field${cityError ? ' shows-search-field--error' : ''}`} ref={cityFieldRef}>
              <i className="fa-sharp fa-light fa-location-dot shows-search-field__icon" aria-hidden />
              <div className="shows-search-field__stack">
                <input
                  ref={cityInputRef}
                  type="text"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    setSelectedCity(null);
                    if (cityError) setCityError(null);
                  }}
                  onFocus={() => cityResults.length > 0 && setCityOpen(true)}
                  placeholder="Search by City"
                  autoComplete="off"
                  aria-label="Search by City"
                  aria-invalid={cityError ? true : undefined}
                />
                {cityError && <span className="shows-search-field__error">{cityError}</span>}
              </div>

              {(city.length > 0 || selectedCity) && (
                <button
                  type="button"
                  className="shows-search-field__clear"
                  onClick={handleClearCity}
                  aria-label="Clear city search"
                >
                  <i className="fa-solid fa-xmark" aria-hidden />
                </button>
              )}

              <AnchoredPortal anchorRef={cityFieldRef} open={cityOpen}>
                <ul className="shows-search-suggestions shows-search-suggestions--portal" ref={cityDropdownRef}>
                  {cityResults.length === 0 ? (
                    <li className="shows-search-suggestions__state shows-search-suggestions__state--empty">City not available</li>
                  ) : (
                    cityResults.map((s) => (
                      <li key={s.id}>
                        <button type="button" className="shows-search-suggestion" onClick={() => selectCity(s)}>
                          <span className="shows-search-suggestion__icon">
                            <i className="fa-sharp fa-light fa-location-dot" aria-hidden />
                          </span>
                          <span className="shows-search-suggestion__text">
                            <span className="shows-search-suggestion__main">{s.city}</span>
                            {(s.state || s.country) && (
                              <span className="shows-search-suggestion__sub">
                                {[s.state, s.country].filter(Boolean).join(', ')}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              </AnchoredPortal>
            </div>

            <div className={`shows-search-field${!hasCity ? ' shows-search-field--disabled' : ''}`} ref={showFieldRef}>
              <i className="fa-light fa-magnifying-glass shows-search-field__icon" aria-hidden />
              <input
                ref={showInputRef}
                type="text"
                value={show}
                onChange={(e) => {
                  // Not `disabled` (see doc comment above) — the field stays
                  // focusable/clickable, this just drops keystrokes while no
                  // city is set so the value never actually changes.
                  if (!hasCity) return;
                  const next = e.target.value;
                  setShow(next);
                  setSelectedShow(false);
                  // Deleting the keyword by hand is the same intent as the ×
                  // button — restore the unfiltered listing.
                  if (!next.trim()) clearShowQueryFilter();
                }}
                onFocus={() => showResults.length > 0 && setShowOpen(true)}
                placeholder="Search by Show Name"
                autoComplete="off"
                aria-label="Search by Show Name"
              />

              {show.length > 0 && (
                <button
                  type="button"
                  className="shows-search-field__clear"
                  onClick={handleClearShow}
                  aria-label="Clear show search"
                >
                  <i className="fa-solid fa-xmark" aria-hidden />
                </button>
              )}

              <AnchoredPortal anchorRef={showFieldRef} open={showOpen}>
                <ul
                  className="shows-search-suggestions shows-search-suggestions--portal shows-search-suggestions--grow"
                  ref={showDropdownRef}
                >
                  {showResults.length === 0 ? (
                    <li className="shows-search-suggestions__state shows-search-suggestions__state--empty">Show not available</li>
                  ) : (
                    <>
                      {showResults.map((s) => (
                        <li key={s.id}>
                          <button type="button" className="shows-search-suggestion" onClick={() => selectShow(s)}>
                            <span className="shows-search-suggestion__icon shows-search-suggestion__icon--thumb">
                              <img
                                src={s.image || FALLBACK_IMAGE}
                                alt=""
                                onError={(e) => {
                                  if (e.currentTarget.src !== FALLBACK_IMAGE) e.currentTarget.src = FALLBACK_IMAGE;
                                }}
                              />
                            </span>
                            <span className="shows-search-suggestion__text">
                              <span className="shows-search-suggestion__main">{s.name}</span>
                              {s.category && <span className="shows-search-suggestion__sub">{s.category}</span>}
                            </span>
                          </button>
                        </li>
                      ))}
                      {/* CMS parity (`theatreLandingPage.js`): when the query
                          matched MORE shows than the capped list renders, a
                          final row with a right arrow + "Show all results"
                          runs the full listing search instead of opening a
                          single show's detail page. */}
                      {showHasMore && (
                        <li>
                          <button
                            type="button"
                            className="shows-search-suggestion shows-search-suggestion--all"
                            onClick={handleShowAllResults}
                          >
                            <span className="shows-search-suggestion__icon">
                              <i className="fa-regular fa-arrow-right-long" aria-hidden />
                            </span>
                            <span className="shows-search-suggestion__text">
                              <span className="shows-search-suggestion__main">Show all results</span>
                            </span>
                          </button>
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </AnchoredPortal>
            </div>

            <button type="submit" className="shows-search-submit">
              Search
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
